import { z } from 'zod';

import { OrganizationSchema } from './organizations';
import { RoleSchema } from './rbac';

export enum AccountType {
  USER = 'user',
  SERVICE = 'service',
}

export const UserSchema = z.object({
  id: z.string(),
  name: z.string(),
  email: z.string(),
  accountType: z.enum(AccountType),
  createdAt: z.iso.datetime(),
  updatedAt: z.iso.datetime(),
  isAdmin: z.boolean().optional(),
  lastActivityAt: z.iso.datetime().optional(),
});

export const UserWithRoleSchema = UserSchema.extend({
  role: RoleSchema.nullable(),
});

export const TokenScopeSchema = z.object({
  type: z.enum(['org-wide', 'team-scoped']),
  teamId: z.string().optional(),
  teamName: z.string().optional(),
});

export type TokenScopeDTO = z.infer<typeof TokenScopeSchema>;

export const MeSchema = z.object({
  user: UserSchema,
  organization: OrganizationSchema.optional(),
  app: z.object({
    url: z.string(),
  }),
  isAdmin: z.boolean(),
  hasActiveLicense: z.boolean(),
  pylonEmailHash: z.string().optional(),
  tokenScope: TokenScopeSchema.optional(),
});

export type MeDTO = z.infer<typeof MeSchema>;

export const RequestPasswordResetSchema = z.object({
  email: z.email('Invalid email format'),
});

export type RequestPasswordResetRequest = z.infer<typeof RequestPasswordResetSchema>;

export type UserDTO = z.infer<typeof UserSchema>;

// User ability schemas
export const UserAbilitySchema = z.object({
  action: z.string(),
  subject: z.string(),
  conditions: z.record(z.string(), z.any()).optional(),
  inverted: z.boolean().optional(),
  reason: z.string().optional(),
});

export type UserAbilityDTO = z.infer<typeof UserAbilitySchema>;

// User status schemas
export enum UserStatus {
  OK = 'ok',
  EXCEEDED_LIMIT = 'exceeded_limit',
  SHOW_USAGE_WARNING = 'show_usage_warning',
  RISKY_EMAIL = 'risky_email',
  DISPOSABLE_EMAIL = 'disposable_email',
  EMAIL_VERIFICATION_REQUIRED = 'email_verification_required',
}

export const UserStatusSchema = z.object({
  status: z.enum(UserStatus),
  error: z.string().optional(),
  message: z.string().optional(),
});

export type UserStatusDTO = z.infer<typeof UserStatusSchema>;

// Login request schema
export const LoginRequestSchema = z.object({
  email: z.string(),
  organizationId: z.string().optional(),
});

export type LoginRequestDTO = z.infer<typeof LoginRequestSchema>;

// Switch organization request schema
export const SwitchOrganizationSchema = z.object({
  organizationId: z.string(),
});

export type SwitchOrganizationRequest = z.infer<typeof SwitchOrganizationSchema>;

// Switch organization response schema
export const SwitchOrganizationResponseSchema = z.object({
  organization: OrganizationSchema,
  message: z.string(),
});

export type SwitchOrganizationResponse = z.infer<typeof SwitchOrganizationResponseSchema>;

// The invite code of an evaluation share link. The field is named as the login request names it,
// which is also the name the server leaves out of request logs.
export const InviteCodeSchema = z.object({
  invite_code: z.string().min(1),
});

export type InviteCodeRequest = z.infer<typeof InviteCodeSchema>;

/** The account shown on the invitation card must still own the accepting browser session. */
export const AcceptInviteRequestSchema = InviteCodeSchema.extend({
  expectedUserId: z.uuid(),
});

export type AcceptInviteRequest = z.infer<typeof AcceptInviteRequestSchema>;

/** A stale invitation card must obtain consent again after the browser changes accounts. */
export const INVITE_ACCOUNT_CHANGED_CODE = 'invite_account_changed';

export const InviteAccountChangedResponseSchema = z.object({
  error: z.string(),
  code: z.literal(INVITE_ACCOUNT_CHANGED_CODE),
});

export type InviteAccountChangedResponse = z.infer<typeof InviteAccountChangedResponseSchema>;

// What a signed-in user is shown before answering an invite: only the organization's name.
export const InvitePreviewResponseSchema = z.object({
  organization: z.object({
    id: z.string(),
    name: z.string(),
  }),
  isMember: z.boolean(),
});

export type InvitePreviewResponse = z.infer<typeof InvitePreviewResponseSchema>;

export const AcceptInviteResponseSchema = z.object({
  organization: OrganizationSchema,
});

/** Marks a 403 as the organization refusing the user, among the other reasons for a 403. */
export const INVITE_REFUSED_CODE = 'invite_refused';

// The 403 that preview and accept answer when the organization, not the link, is the reason a
// user cannot join. Other 403s on these routes, such as a rejected request origin, say nothing
// about the invite, so a client tells this one apart by its code.
export const InviteRefusalResponseSchema = z.object({
  error: z.string(),
  code: z.literal(INVITE_REFUSED_CODE),
  reason: z.enum(['removed', 'email_domain']),
});

export type InviteRefusalResponse = z.infer<typeof InviteRefusalResponseSchema>;

export type AcceptInviteResponse = z.infer<typeof AcceptInviteResponseSchema>;
